<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="700px"
    style="z-index: 200000001;"
    transition="dialog-bottom-transition"
  >
    <v-card
      class="icon-dialog-card"
      elevation="24"
      shaped
    >
      <!-- Header with gradient background -->
      <v-card-title class="dialog-header pa-6">
        <div class="header-content">
          <div class="header-icon-wrapper">
            <v-icon size="28" color="white" class="header-icon">
              {{ type === 'Logo' ? 'mdi-image-outline' : 'mdi-star-outline' }}
            </v-icon>
          </div>
          <div class="header-text">
            <h2 class="header-title">Add {{ type }} Icon</h2>
            <p class="header-subtitle">Create stunning visual identity</p>
          </div>
        </div>
        <v-btn
          icon
          @click="onClose"
          class="close-btn"
          size="large"
        >
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Description section -->
      <v-card-text class="description-section pa-6">
        <div class="description-card">
          <v-icon color="primary" size="20" class="mr-2">mdi-information-outline</v-icon>
          <span class="description-text">
            Add your {{ type.toLowerCase() }} icons that will be used on the site.
            We highly recommend a 512x512 SVG file for optimal quality.
          </span>
        </div>
      </v-card-text>

      <!-- Image upload section -->
      <v-card-text class="upload-section pa-6">
        <div class="section-header">
          <h3 class="section-title">
            <v-icon color="primary" size="20" class="mr-2">mdi-upload</v-icon>
            Select Icon
          </h3>
        </div>
        
        <div class="upload-wrapper">
          <div class="upload-container">
            <editable-rect-avatar
              :image="profile.image"
              :onImageSelected="onImageSelected"
              class="avatar-component"
            />
            <div class="upload-hint">
              <v-icon color="grey" size="16" class="mr-1">mdi-cloud-upload</v-icon>
              <span>Click to upload or drag & drop</span>
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- Form section -->
      <v-card-text class="form-section pa-6">
        <v-row>
          <v-col cols="12" md="6">
            <div class="input-group">
              <label class="input-label">
                <v-icon color="primary" size="18" class="mr-2">mdi-tag-outline</v-icon>
                Icon Name
              </label>
              <v-text-field
                v-model="profile.name"
                placeholder="Enter icon name"
                solo
                flat
                outlined
                class="custom-input"
                :rules="[rules.required]"
                @input="onChange"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="grey">mdi-rename-box</v-icon>
                </template>
              </v-text-field>
            </div>
          </v-col>
          
          <v-col cols="12" md="6">
            <div class="input-group">
              <label class="input-label">
                <v-icon color="primary" size="18" class="mr-2">mdi-palette-outline</v-icon>
                Color Code
              </label>
              <v-text-field
                v-model="profile.color"
                placeholder="#b5e539"
                solo
                flat
                outlined
                class="custom-input"
                :rules="[rules.required]"
                @input="onChange"
              >
                <template v-slot:prepend-inner>
                  <div 
                    class="color-preview" 
                    :style="{ backgroundColor: profile.color || '#e0e0e0' }"
                  ></div>
                </template>
              </v-text-field>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Error message -->
      <v-card-text v-if="message" class="pa-6 pt-0">
        <v-alert
          type="error"
          dense
          outlined
          class="error-alert"
          transition="slide-y-transition"
        >
          <v-icon slot="prepend" color="error">mdi-alert-circle-outline</v-icon>
          {{ message }}
        </v-alert>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="action-section pa-6">
        <v-spacer></v-spacer>
        <v-btn
          text
          @click="onClose"
          class="cancel-btn"
          large
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="onSave"
          class="save-btn"
          large
          elevation="2"
          :loading="loading"
          :disabled="!profile.name || !profile.color"
        >
          <v-icon left>mdi-content-save</v-icon>
          Save Icon
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";
import EditableRectAvatar from "@/components/EditableRectAvatar.vue";

export default {
  components: { EditableRectAvatar },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    type: String,
    onClose: {
      type: Function
    }
  },
  data() {
    return {
      code: "",
      name: "",
      message: null,
      loading: false,
      sendingCode: false,
      disableResend: false,
      resendCount: 0,
      rules: {
        required: value => !!value || "Required."
      },
      selectedImage: null,
      profile: {}
    };
  },
  methods: {
    ...mapActions("icon", {
      addIcon: "addIcon"
    }),
    onChange() {
      this.message = null;
    },
    onSave() {
      if (!this.profile.image && !this.selectedImage) {
        this.message = "Please select your icon image.";
        return;
      }
      if (!this.profile.name) {
        this.message = "Please add the icon name.";
        return;
      }
      if (!this.profile.color) {
        this.message = "Please add the color code.";
        return;
      }

      this.message = null;
      var formData = new FormData();
      
      if (this.selectedImage) {
        formData.append("image", this.selectedImage);
      }
      formData.append("name", this.profile.name);
      formData.append("color", this.profile.color);
      formData.append("type", this.type);

      this.loading = true;
      this.addIcon(formData)
        .then(() => {
          this.loading = false;
          this.onClose();
        })
        .catch(error => {
          this.loading = false;
          if (error.response) {
            this.message = error.response.data.message;
          } else {
            this.message = error;
          }
        });
    },
    onImageSelected(image) {
      this.selectedImage = image;
      this.message = null;
    }
  }
};
</script>

<style scoped>
.icon-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.dialog-header {
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  color: white;
  position: relative;
  overflow: hidden;
}

.dialog-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="75" cy="75" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="50" cy="10" r="0.5" fill="rgba(255,255,255,0.05)"/></pattern></defs><rect width="100%" height="100%" fill="url(%23grain)"/></svg>');
  opacity: 0.3;
}

.header-content {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
}

.header-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
  margin-right: 16px;
  backdrop-filter: blur(10px);
}

.header-icon {
  display: block;
}

.header-text {
  flex: 1;
}

.header-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  line-height: 1.2;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-subtitle {
  font-size: 14px;
  margin: 4px 0 0 0;
  opacity: 0.9;
  font-weight: 400;
}

.close-btn {
  position: relative;
  z-index: 1;
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(10px);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

.description-section {
  background: white;
}

.description-card {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  border-left: 4px solid #2196f3;
}

.description-text {
  font-size: 14px;
  color: #424242;
  line-height: 1.5;
}

.upload-section {
  background: white;
  border-bottom: 1px solid #e0e0e0;
}

.section-header {
  margin-bottom: 24px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  margin: 0;
}

.upload-wrapper {
  display: flex;
  justify-content: center;
}

.upload-container {
  text-align: center;
}

.avatar-component {
  margin-bottom: 16px;
}

.upload-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #666;
  background: #f8f9fa;
  border-radius: 20px;
  padding: 8px 16px;
  border: 1px dashed #ddd;
}

.form-section {
  background: white;
}

.input-group {
  margin-bottom: 8px;
}

.input-label {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.custom-input {
  margin-top: 0 !important;
}

.custom-input .v-input__slot {
  border-radius: 12px !important;
  background: #f8f9fa !important;
  border: 1px solid #e0e0e0 !important;
}

.custom-input .v-input__slot:hover {
  border-color: #2196f3 !important;
}

.custom-input.v-input--is-focused .v-input__slot {
  border-color: #2196f3 !important;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1) !important;
}

.color-preview {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #e0e0e0;
  margin-right: 8px;
}

.error-alert {
  border-radius: 12px !important;
  background: #fff5f5 !important;
}

.action-section {
  background: #f8f9fa;
  border-top: 1px solid #e0e0e0;
}

.cancel-btn {
  color: #666 !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.save-btn {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%) !important;
  color: white !important;
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 12px !important;
  padding: 0 24px !important;
}

.save-btn:hover {
  background: linear-gradient(135deg, #45a049 0%, #3d8b40 100%) !important;
}

.save-btn:disabled {
  background: #e0e0e0 !important;
  color: #999 !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-icon-wrapper {
    margin-right: 0;
    margin-bottom: 12px;
  }
  
  .header-title {
    font-size: 20px;
  }
  
  .section-title {
    font-size: 16px;
  }
  
  .upload-hint {
    font-size: 12px;
    padding: 6px 12px;
  }
  
  .action-section {
    flex-direction: column;
    gap: 12px;
  }
  
  .cancel-btn,
  .save-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .dialog-header {
    padding: 16px !important;
  }
  
  .description-section,
  .upload-section,
  .form-section,
  .action-section {
    padding: 16px !important;
  }
  
  .header-title {
    font-size: 18px;
  }
  
  .header-subtitle {
    font-size: 12px;
  }
}

/* Animation enhancements */
.v-dialog__content {
  align-items: center;
}

.icon-dialog-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.custom-input .v-input__slot {
  transition: all 0.2s ease;
}

.save-btn,
.cancel-btn {
  transition: all 0.2s ease;
}

.upload-container:hover .upload-hint {
  background: #e3f2fd;
  border-color: #2196f3;
  color: #1976d2;
}

/* Loading state */
.save-btn.v-btn--loading {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%) !important;
}
</style>

